import { useEffect, useState } from "react";
import { createThemeController, Theme, ThemeController } from "./theme";

/*
  One controller for the whole app: it owns the single matchMedia subscription and the
  single source of truth for the stamped attribute. Created lazily so importing this
  module — in a test, or in a tree that never renders a toggle — touches nothing.
*/
let controller: ThemeController | null = null;

export const getThemeController = () => {
  if (!controller) {
    controller = createThemeController();
  }
  return controller;
};

export interface UseThemeResult {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

/*
  The hook is deliberately thin — every rule it obeys is tested on the controller, since
  component tests do not run here (spec §2).
*/
export const useTheme = (): UseThemeResult => {
  const themeController = getThemeController();
  const [theme, setThemeState] = useState<Theme>(themeController.getTheme());

  useEffect(() => {
    setThemeState(themeController.getTheme());
    return themeController.subscribe(setThemeState);
  }, [themeController]);

  return {
    theme,
    setTheme: themeController.setTheme,
    toggleTheme: themeController.toggleTheme,
  };
};
